import { Button, Form, Input, Table, Tooltip } from "antd";
import { useContext, useEffect, useState } from "react";
import { ExportToExcel } from "../../utils/exportExcel";

import { useQuery } from "@tanstack/react-query";
import { handleExportUsers } from "../../api/users";
import Something from "../../components/shared/Something";
import { ColumnsType } from "antd/es/table";
import Error from "../../components/shared/Error";
import { AuthContext } from "../../contexts/AuthContext";
import { handleFetchUnits } from "../../api/units";
import {
  CrownFilled,
  DeleteOutlined,
  EditFilled,
  FileExcelOutlined,
  FilterOutlined,
  ReloadOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import CreateUnit from "./components/CreateUnit";
import DeleteUnit from "./components/DeleteUnit";
import UpdateUnit from "./components/UpdateUnit";
import AssignApprover from "./components/AssignApprover";

const { Search } = Input;

export default function Units() {
  const { user } = useContext(AuthContext);
  const [selected, setSelected] = useState<any>({});
  const [createModal, setCreateModal] = useState<boolean>(false);
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [assignModal, setAssignModal] = useState<boolean>(false);
  const [isExporting, setExporting] = useState<boolean>(false);
  const [form] = Form.useForm();
  const access = [9];

  const initials = {
    name: "",
  };

  const [filters, setFilters] = useState<any>({ ...initials });

  const params = `name=${filters?.name}`;

  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });

  const {
    data: units,
    refetch,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["units", pagination.page, pagination.limit, params],
    queryFn: () =>
      handleFetchUnits({
        page: pagination.page,
        limit: pagination.limit,
        params,
      }),
  });

  const handlePageChange = (e: any) => {
    setPagination((prev: any) => ({
      ...prev,
      page: e.current,
      limit: e.pageSize,
    }));
  };

  const clearAllFilters = async () => {
    form.resetFields();
    setFilters({ ...initials });
    handleRefetch();
  };
  const handleRefetch = () => {
    refetch();
  };

  interface DataType {
    key: React.Key;
    name: string;
    age: number;
    address: string;
  }
  const getColumns = (user: any, access: any) => {
    const columns: ColumnsType<DataType> = [
      {
        title: "S/N",
        width: 60,
        render: (record: any, text: any, index: any) => (
          <span className="text-gray-700 ml-2">
            {pagination?.page * pagination?.limit -
              pagination?.limit +
              index +
              1}
          </span>
        ),
      },
      {
        title: "NAME",
        width: 360,
        render: (record: any) => {
          return (
            <div className="text-gray-700">
              <div className="">{record?.name || "-"}</div>
              {record?.isAdmin && (
                <div className="flex items-center">
                  <div className="">
                    <CrownFilled color="#16a34a" />
                  </div>
                  <div className="mx-1 text-green-600">Admin</div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "APPROVER",
        width: 160,
        render: (record: any) => {
          return (
            <div className="text-gray-700">
              {record?.approver ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="">
                      <CrownFilled color="#16a34a" />
                    </div>
                    <div className="mx-1 text-green-600">
                      {record?.approver?.name}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelected(record);
                      setAssignModal(true);
                    }}
                    className="border w-28 py-1 bg-blue-50 shadow-sm flex items-center justify-center"
                  >
                    <SwapOutlined />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setSelected(record);
                    setAssignModal(true);
                  }}
                  className="border w-28 py-1 bg-blue-50 shadow-sm flex items-center justify-center"
                >
                  + Assign
                </button>
              )}
            </div>
          );
        },
      },
      {
        title: "ACTION",
        width: 100,
        fixed: "right",
        render: (record: any) => (
          <div className="flex items-center">
            <button
              onClick={() => {
                setSelected(record);
                setUpdateModal(true);
              }}
              className="border py-1 px-2 mx-1 flex items-center bg-yellow-50 text-yellow-800 shadow-sm hover:bg-yellow-100"
            >
              <EditFilled />
            </button>

            <button
              onClick={() => {
                setSelected(record);
                setDeleteModal(true);
              }}
              className="border py-1 px-2 mx-1 flex items-center bg-red-50 text-red-800 shadow-sm hover:bg-red-100"
            >
              <DeleteOutlined />
            </button>
          </div>
        ),
      },
    ];
    return columns.filter((column: any) => {
      if (column.title === "ACTION") {
        return access.includes(user?.accessLevel);
      }
      return true;
    });
  };

  const columns = getColumns(user, access);

  const exportUsers = async () => {
    setExporting(true);
    const users = await handleExportUsers();
    await ExportToExcel(
      users?.map((item: any) => {
        return {
          EMP_ID: item?.empId || "-",
          EMAIL: item?.email || "-",
          FULLNAME: item?.name || "-",
          BUSINESS_UNIT: item?.businessUnit || "-",
          LEGAL_ENTITY: item?.legalEntity || "-",
          MOBILE: item?.mobile || "-",
        };
      }),
      "Users.xlsx"
    );
    setExporting(false);
  };

  useEffect(() => {
    setPagination({ ...pagination, total: units?.total });
  }, [units]);

  if (!access.includes(user?.accessLevel)) {
    return <Error error="You are not authorized to access this page" />;
  }

  return (
    <div className="bg-gray-100">
      <div className="bg-white m-1">
        <div className="flex flex-col sm:flex-row justify-between border-b p-2">
          <div className="">
            <div className="font-bold text-lg">Business Units</div>
            <div className="text-gray-600">Manage business units</div>
          </div>

          <div className="flex">
            <div className="flex">
              <Form layout="inline" form={form}>
                <Form.Item name="name">
                  <Search
                    placeholder="Search name"
                    className="rounded-none"
                    allowClear
                    onSearch={(e: any) => {
                      setFilters({ ...filters, name: e });
                    }}
                  />
                </Form.Item>
              </Form>
              <Button
                className="ml-2 focus:outline-none hover:bg-red-100 rounded-none shadow-sm"
                onClick={() => {
                  clearAllFilters();
                }}
              >
                <FilterOutlined color="gray" />
              </Button>
            </div>
            <Tooltip title="Export To Excel">
              <button
                className="flex mx-2 justify-center items-center border px-4 h-8  hover:bg-gray-100 shadow-sm"
                onClick={() => exportUsers()}
              >
                {isExporting ? (
                  <ReloadOutlined />
                ) : (
                  <FileExcelOutlined size={20} />
                )}
              </button>
            </Tooltip>
            {access.includes(user?.accessLevel) && (
              <div className="">
                <button
                  onClick={() => setCreateModal(true)}
                  className="flex mx-2 justify-center items-center border px-4 h-8 bg-karimjee hover:bg-karimjee-dark text-white shadow-sm"
                >
                  + New
                </button>
              </div>
            )}
          </div>
        </div>
        {isError ? (
          <Something refetch={refetch} isFetching={isFetching} />
        ) : (
          <div className="border p-2">
            <Table
              size="small"
              dataSource={units?.records}
              columns={columns}
              scroll={{ y: 660, x: 0 }}
              rowKey="id"
              bordered
              pagination={{
                current: pagination?.page,
                pageSize: pagination?.limit,
                total: pagination?.total,
              }}
              onChange={(e: any) => handlePageChange(e)}
              loading={isLoading}
            />
          </div>
        )}
      </div>

      <CreateUnit
        isVisible={createModal}
        setVisible={setCreateModal}
        selected={selected}
        refetch={refetch}
      />

      <UpdateUnit
        isVisible={updateModal}
        setVisible={setUpdateModal}
        selected={selected}
      />

      <AssignApprover
        isVisible={assignModal}
        setVisible={setAssignModal}
        selected={selected}
      />

      <DeleteUnit
        isVisible={deleteModal}
        setVisible={setDeleteModal}
        selected={selected}
      />
    </div>
  );
}
